import { supabase } from './supabaseClient';

// A report value counts as "entered" when it is a non-blank string. Unlike the
// numeric PD/KPI bundles, 0 is not meaningful here — values are free text — so
// '' / null / undefined are "left blank" and produce no row (mirrors the
// performance bundle's blank-skip rule, adapted for text).
const isEntered = (v) => v !== '' && v !== null && v !== undefined;

export const reportService = {
  // The user's active report-system assignments (RLS returns own rows).
  // Mirrors performanceService.getAssignedPISystems.
  async getAssignedReportSystems(userId) {
    const { data, error } = await supabase
      .from('user_report_system_assignments')
      .select('*, report_systems(*)')
      .eq('user_id', userId)
      .eq('is_active', true);
    if (error) throw error;
    return data;
  },

  // Active report fields for one report system, ordered by sort_order.
  async getFormFields(reportSystemId) {
    const { data, error } = await supabase
      .from('report_fields')
      .select('*')
      .eq('report_system_id', reportSystemId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Countries for the single-choice "dropdown" report field. Any authenticated
  // user can read (RLS view_countries). Resilient: a countries hiccup must not
  // break loading a text-only report, so return [] on error.
  async getCountries() {
    const { data, error } = await supabase
      .from('countries')
      .select('id, name, code')
      .order('name', { ascending: true });
    if (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
    return data;
  },

  // Existing report_content line-items for one submission (mirror getSubmissionPerformance).
  async getSubmissionReport(submissionId) {
    const { data, error } = await supabase
      .from('report_content')
      .select('*')
      .eq('submission_id', submissionId);
    if (error) throw error;
    return data;
  },

  // Upsert one report_content row per non-blank item, keyed on
  // (submission_id, report_field_id) — targets the NON-PARTIAL unique index
  // uq_report_submission_field so PostgREST onConflict resolves it.
  async saveSubmissionReport(submissionId, userId, items) {
    const rows = items
      .filter((it) => isEntered(it.value))
      .map((it) => ({
        submission_id: submissionId,
        user_id: userId,
        report_field_id: it.report_field_id,
        value: it.value,
      }));
    if (rows.length === 0) return [];
    const { data, error } = await supabase
      .from('report_content')
      .upsert(rows, { onConflict: 'submission_id,report_field_id' })
      .select();
    if (error) throw error;
    return data;
  },
};
